"use client";

import * as React from "react";
import { useState } from "react";
import Image from "next/image";
import Link from "next/link";
import SideTabbedButton from "@/components/generic/SideTabbedButton";
import { PlainContentItem } from "../generic/plainContentItem";
import { Stack, Typography, Button, IconButton, Box, Container, Divider } from "@mui/material";
import MobileMenu from "@/components/layout/mobileMenu";
import theme, { blueGradientSxDown } from "@/theme/theme";

import logo from "@/assets/images/logo.webp";
import arrow from "@/assets/images/icons/arrow.webp"

import menuSmartContracts from "@/assets/images/icons/menu-smart-contracts.webp"
import menuInfra from "@/assets/images/icons/menu-infrastructure.webp"
import menuAI from "@/assets/images/icons/menu-ai.webp"
import menuDev from "@/assets/images/icons/menu-dev.webp"
import menuGTM from "@/assets/images/icons/menu-gtm.webp"
import menuZKPQC from "@/assets/images/icons/menu-zk-pqc.webp"

import menuLaunch from "@/assets/images/icons/menu-launch.webp"
import menuExplore from "@/assets/images/icons/menu-explore.webp"

import menuTeam from "@/assets/images/icons/menu-team.webp"
import menuPortfolio from "@/assets/images/icons/menu-portfolio.webp"

import mobileMenuButton from "@/assets/images/mobileMenuButton.webp"
import mobileContactButtonBg from "@/assets/images/mobileContactButtonBg.webp"

import mobileMenuOpen from "@/assets/images/icons/mobileMenuOpen.svg?url";
import mobileMenuClose from "@/assets/images/icons/mobileMenuClose.svg?url";

import {
    NavigationMenu,
    NavigationMenuList,
    NavigationMenuItem,
    NavigationMenuTrigger,
    NavigationMenuContent,
    NavigationMenuLink,
    NavigationMenuContentItem,
} from "@/components/generic/navigationMenu";

type NavItem = {
    label: string;
    href: string;
    disabled?: boolean;
    children?: NavItem[];
};

const mobileMenuItems: NavItem[] = [
    { label: "Home", href: "/" },
    {
        label: "Services",
        href: "/services",
        children: [
            { label: "Smart Contracts", href: "/services#smart-contracts" },
            { label: "Infra & Protocols", href: "/services#infra-and-protocols" },
            { label: "AI Systems", href: "/services#ai-systems" },
            { label: "Product Dev", href: "/services#product-dev" },
            { label: "ZK & PQC", href: "/services#zk-and-pqc" },
            { label: "GTM & Legal", href: "/services#gtm-and-legal" },
            { label: "Quantum Readiness", href: "/quantum-readiness" },
        ],
    },
    {
        label: "About us",
        href: "/team",
        children: [
            { label: "Team", href: "/team" },
            { label: "Careers", href: "/vacancies" },
            { label: "Portfolio", href: "/portfolio" },
        ],
    },
    { label: "Blog", href: "/blog" },
    { label: "SiteLab", href: "https://www.sitelab.build/" },
    { label: "BlockAI", href: "/blockai", disabled: true },
];


export default function Header() {

    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

    return (
        <Box component="header" sx={{ zIndex: 50, width: "100%", bgcolor: "transparent", position: "relative" }}>
            <Container 
                maxWidth="lg"
                sx={{ 
                    display: "flex", 
                    height: 80, 
                    alignItems: "center", 
                    justifyContent: "space-between", 
                    px: { xs: 2, md: 3, lg: 6 } 
                }}
            >
            <Link href="/" style={{ textDecoration: 'none', display: 'block' }}>
                <Box sx={{ width: { xs: '100px', lg: '120px' }, height: 'auto', cursor: 'pointer' }}>
                    <Image 
                        src={logo} 
                        alt="mood global services mgs logo" 
                        width={120} 
                        height={27} 
                        priority 
                        style={{ width: '100%', height: 'auto' }}
                    />
                </Box>
            </Link>
                <Box sx={{ display: { xs: 'none', lg: 'block' } }}>
                    <NavigationMenu>
                        <NavigationMenuList>
                            <NavigationMenuItem>
                                <NavigationMenuLink href="/" className="nav-pill">
                                    <Typography component={"span"} variant="h6">
                                        Home
                                    </Typography>
                                </NavigationMenuLink>
                            </NavigationMenuItem>

                            <NavigationMenuItem>
                                <NavigationMenuTrigger className="nav-pill">
                                    <Typography component={"span"} variant="h6">
                                        Services
                                    </Typography>
                                </NavigationMenuTrigger>
                                <NavigationMenuContent>
                                    <Box sx={{ display: "grid", width: 800, gridTemplateColumns: "repeat(3, 1fr)", gap: 1.5 }}>
                                        <NavigationMenuContentItem href="/services#smart-contracts">
                                            <Stack gap={1}>
                                                <Stack direction={"row"} alignItems={"center"} gap={0.5}>
                                                    <Image src={menuSmartContracts} height={20} width={20} alt="mgs mood global services Smart Contracts" priority></Image>
                                                    <Typography component="span" variant="body2">Smart Contracts</Typography>
                                                </Stack>
                                                <Typography component="span" variant="body2" color={theme.palette.text.secondary}>
                                                    End-to-end development from
                                                    chain setup to dApp deployment.
                                                </Typography>
                                            </Stack>
                                        </NavigationMenuContentItem>
                                        <NavigationMenuContentItem href="/services#infra-and-protocols">
                                            <Stack gap={1}>
                                                <Stack direction={"row"} alignItems={"center"} gap={0.5}>
                                                    <Image src={menuInfra} height={20} width={20} alt="mgs mood global services Infra & Protocols" priority></Image>
                                                    <Typography component="span" variant="body2">Infra & Protocols</Typography>
                                                </Stack>
                                                <Typography component="span" variant="body2" color={theme.palette.text.secondary}>
                                                    We design and implement robust blockchain infrastructure
                                                </Typography>
                                            </Stack>
                                        </NavigationMenuContentItem>
                                        <NavigationMenuContentItem href="/services#ai-systems">
                                            <Stack gap={1}>
                                                <Stack direction={"row"} alignItems={"center"} gap={0.5}>
                                                    <Image src={menuAI} height={20} width={20} alt="mgs mood global services AI Systems" priority></Image>
                                                    <Typography component="span" variant="body2">AI Systems</Typography>
                                                </Stack>
                                                <Typography component="span" variant="body2" color={theme.palette.text.secondary}>
                                                    Automation, insight generation, and data-driven decision-making
                                                </Typography>
                                            </Stack>
                                        </NavigationMenuContentItem>
                                        <NavigationMenuContentItem href="/services#product-dev">
                                            <Stack gap={1}>
                                                <Stack direction={"row"} alignItems={"center"} gap={0.5}>
                                                    <Image src={menuDev} height={20} width={20} alt="mgs mood global services Product Dev" priority></Image>
                                                    <Typography component="span" variant="body2">Product Dev</Typography>
                                                </Stack>
                                                <Typography component="span" variant="body2" color={theme.palette.text.secondary}>
                                                    Blockchain-based product development and UX/Ui design
                                                </Typography>
                                            </Stack>
                                        </NavigationMenuContentItem>
                                        <NavigationMenuContentItem href="/services#gtm-and-legal">
                                            <Stack gap={1}>
                                                <Stack direction={"row"} alignItems={"center"} gap={0.5}>
                                                    <Image src={menuGTM} height={20} width={20} alt="mgs mood global services GTM & Legal" priority></Image>
                                                    <Typography component="span" variant="body2">GTM & Legal</Typography>
                                                </Stack>
                                                <Typography component="span" variant="body2" color={theme.palette.text.secondary}>
                                                    Market insight with<br />legal precision
                                                </Typography>
                                            </Stack>
                                        </NavigationMenuContentItem>
                                        <NavigationMenuContentItem href="/services#zk-and-pqc">
                                            <Stack gap={1}>
                                                <Stack direction={"row"} alignItems={"center"} gap={0.5}>
                                                    <Image src={menuZKPQC} height={20} width={20} alt="mgs mood global services ZK & PQC" priority></Image>
                                                    <Typography component="span" variant="body2">ZK & PQC</Typography>
                                                </Stack>
                                                <Typography component="span" variant="body2" color={theme.palette.text.secondary}>
                                                    Advanced criptography<br />solutions with ZK & PQC
                                                </Typography>
                                            </Stack>
                                        </NavigationMenuContentItem>
                                        <NavigationMenuContentItem href="/quantum-readiness">
                                            <Stack gap={1}>
                                                <Stack direction={"row"} alignItems={"center"} gap={0.5}>
                                                    <Image src={menuZKPQC} height={20} width={20} alt="mgs mood global services Quantum Readiness" priority></Image>
                                                    <Typography component="span" variant="body2">Quantum Readiness</Typography>
                                                </Stack>
                                                <Typography component="span" variant="body2" color={theme.palette.text.secondary}>
                                                    Get PQC-ready with our<br />AI compliance framework
                                                </Typography>
                                            </Stack>
                                        </NavigationMenuContentItem>
                                    </Box>
                                </NavigationMenuContent>
                            </NavigationMenuItem>

                            <NavigationMenuItem>
                                <NavigationMenuTrigger className="nav-pill">
                                    <Typography component={"span"} variant="h6">
                                        Products
                                    </Typography>
                                </NavigationMenuTrigger>
                                <NavigationMenuContent>
                                    <Box sx={{ display: "grid", width: 800, gridTemplateColumns: "1fr", gap: 1.5 }}>
                                        <NavigationMenuContentItem
                                            href="https://www.sitelab.build/" 
                                            target="_blank" 
                                            rel="noopener noreferrer"
                                        >
                                            <Stack direction={"row"} alignItems={"stretch"} justifyContent={"space-between"} gap={1.5}>
                                                <Stack alignItems={"center"} justifyContent={"center"} sx={{ overflow: "hidden", width: "15%" }}>
                                                    <Image src={menuLaunch} width={80} height={80} alt="launch" priority style={{
                                                        width: "100%",
                                                        height: "100%"
                                                    }}></Image>
                                                </Stack>
                                                <Stack gap={1} width={"70%"} height={"100%"} justifyContent={"space-between"}>
                                                    <Typography component="span" variant="subtitle1">
                                                        <Box
                                                            component="span"
                                                            sx={{
                                                                ...blueGradientSxDown(theme),
                                                                WebkitBackgroundClip: "text",
                                                                WebkitTextFillColor: "transparent",
                                                            }}
                                                        >
                                                            Launch your business online with SiteLab’s AI
                                                        </Box>
                                                    </Typography>
                                                    <Typography component="span" variant="body2" color={theme.palette.text.secondary}>
                                                        Generated websites, tailored for SMEs. Enjoy quick setup, cost-effective solutions, and no technical expertise needed to create a professional, modern website.
                                                    </Typography>
                                                </Stack>
                                                <Stack flexGrow={1} justifyContent={"end"}>
                                                    <SideTabbedButton color="#1D1D1D" shadowColor="#292929" height={40} hoverShiftX={1.5} hoverShiftY={-1}>
                                                        <Stack direction={"row"} alignItems={"center"} justifyContent={"center"} gap={1}>
                                                            <Typography component="span" variant="h6" marginRight={2.5} sx={{ whiteSpace: "nowrap", display: "inline-block" }}>
                                                                Explore
                                                            </Typography>
                                                            <span className="arrow-clip">
                                                                <span className="arrow-slide">
                                                                    <Image src={arrow} alt="arrow animation" width={20} height={20} style={{ display: "block" }} />
                                                                </span>
                                                            </span>
                                                        </Stack>
                                                    </SideTabbedButton>
                                                </Stack>
                                            </Stack>
                                        </NavigationMenuContentItem>
                                        <NavigationMenuContentItem href="/products/explore">
                                            <Stack direction={"row"} alignItems={"stretch"} justifyContent={"space-between"} gap={1.5}>
                                                <Stack alignItems={"center"} justifyContent={"center"} sx={{ overflow: "hidden", width: "15%" }}>
                                                    <Image src={menuExplore} width={80} height={80} alt="launch" priority style={{
                                                        width: "100%",
                                                        height: "100%"
                                                    }}></Image>
                                                </Stack>
                                                <Stack gap={1} width={"70%"} height={"100%"} justifyContent={"space-between"}>
                                                    <Typography component="span" variant="subtitle1">
                                                        <Box
                                                            component="span"
                                                            sx={{
                                                                ...blueGradientSxDown(theme),
                                                                WebkitBackgroundClip: "text",
                                                                WebkitTextFillColor: "transparent",
                                                            }}
                                                        >
                                                            Explore all EVM-compatible blockchains 
                                                        </Box>
                                                    </Typography>
                                                    <Typography component="span" variant="body2" color={theme.palette.text.secondary}>
                                                        Query wallet addresses, retrieve transaction details, or append custom notes through natural conversation — faster and smarter than traditional tools like Blockscan
                                                    </Typography>
                                                </Stack>
                                                <Stack flexGrow={1} justifyContent={"end"}>
                                                    <SideTabbedButton color="#1D1D1D" shadowColor="#292929" height={40} hoverShiftX={1.5} hoverShiftY={-1}>
                                                        <Stack direction={"row"} alignItems={"center"} justifyContent={"center"} gap={1}>
                                                            <Typography component={"span"} variant="h6" sx={{
                                                                whiteSpace: "nowrap"
                                                            }}>
                                                                Coming soon
                                                            </Typography>
                                                        </Stack>
                                                    </SideTabbedButton>
                                                </Stack>
                                            </Stack>
                                        </NavigationMenuContentItem>
                                    </Box>
                                </NavigationMenuContent>
                            </NavigationMenuItem>

                            <NavigationMenuItem>
                                <NavigationMenuTrigger className="nav-pill">
                                    <Typography component={"span"} variant="h6" sx={{
                                        whiteSpace: "nowrap"
                                    }}>
                                        About us
                                    </Typography>
                                </NavigationMenuTrigger>
                                <NavigationMenuContent>
                                    <Box sx={{ display: "grid", width: 800, height: 275, alignItems: "stretch", gridTemplateColumns: "1fr 1px 1fr 1px 1fr" }}>
                                        <Box sx={{ pr: 1.5 }}>
                                            <PlainContentItem href="/team">
                                                <Stack height={"100%"} justifyContent={"space-between"} paddingX={1.5} paddingTop={0.5} paddingBottom={1.5}>
                                                    <Typography component="span" variant="subtitle1">
                                                        <Box
                                                            component="span"
                                                            sx={{
                                                                ...blueGradientSxDown(theme),
                                                                WebkitBackgroundClip: "text",
                                                                WebkitTextFillColor: "transparent",
                                                            }}
                                                        >
                                                            Team
                                                        </Box>
                                                    </Typography>
                                                    <Stack alignItems={"center"} justifyContent={"center"} paddingX={2}>
                                                        <Image src={menuTeam} width={150} height={75} alt="team"></Image>
                                                    </Stack>
                                                    <SideTabbedButton fullWidth color="#1D1D1D" shadowColor="#292929" height={40} hoverShiftX={1.5} hoverShiftY={-1}>
                                                        <Stack direction={"row"} alignItems={"center"} justifyContent={"center"} gap={1}>
                                                            <Typography component={"span"} variant="h6" sx={{
                                                                whiteSpace: "nowrap"
                                                            }}>
                                                                View
                                                            </Typography>
                                                        </Stack>
                                                    </SideTabbedButton>
                                                </Stack>
                                            </PlainContentItem>
                                        </Box>
                                        <Divider orientation="vertical" flexItem sx={{ borderColor: "#292929" }} />
                                        <Box sx={{ px: 1.5 }}>
                                            <PlainContentItem href="/vacancies">
                                                <Stack height={"100%"} justifyContent={"space-between"} paddingX={1} paddingTop={0.5} paddingBottom={1.5}>
                                                    <Typography component="span" variant="subtitle1">
                                                        <Box
                                                            component="span"
                                                            sx={{
                                                                ...blueGradientSxDown(theme),
                                                                WebkitBackgroundClip: "text",
                                                                WebkitTextFillColor: "transparent",
                                                            }}
                                                        >
                                                            Careers
                                                        </Box>
                                                    </Typography>
                                                    <Typography component="span" variant="body2" textAlign="center" sx={{ maxWidth: "180px", lineHeight: 1.6, fontWeight: 300 }}>
                                                        Check out our open roles and become part of a team that’s shaping the future
                                                    </Typography>
                                                    <SideTabbedButton fullWidth color="#1D1D1D" shadowColor="#292929" height={40} hoverShiftX={1.5} hoverShiftY={-1}>
                                                        <Stack direction={"row"} alignItems={"center"} justifyContent={"center"} gap={1}>
                                                            <Typography component={"span"} variant="h6" sx={{
                                                                whiteSpace: "nowrap"
                                                            }}>
                                                                Join the team
                                                            </Typography>
                                                        </Stack>
                                                    </SideTabbedButton>
                                                </Stack>
                                            </PlainContentItem>
                                        </Box>
                                        <Divider orientation="vertical" flexItem sx={{ borderColor: "#292929" }} />
                                        <Box sx={{ pl: 1.5 }}>
                                            <PlainContentItem href="/portfolio">
                                                <Stack height={"100%"} justifyContent={"space-between"} paddingX={1.5} paddingTop={0.5} paddingBottom={1.5}>
                                                    <Typography component="span" variant="subtitle1">
                                                        <Box
                                                            component="span"
                                                            sx={{
                                                                ...blueGradientSxDown(theme),
                                                                WebkitBackgroundClip: "text",
                                                                WebkitTextFillColor: "transparent",
                                                            }}
                                                        >
                                                            Portfolio
                                                        </Box>
                                                    </Typography>
                                                    <Stack alignItems={"center"} justifyContent={"center"} paddingX={2}>
                                                        <Image src={menuPortfolio} width={150} height={75} alt="team"></Image>
                                                    </Stack>
                                                    <SideTabbedButton fullWidth color="#1D1D1D" shadowColor="#292929" height={40} hoverShiftX={1.5} hoverShiftY={-1}>
                                                        <Stack direction={"row"} alignItems={"center"} justifyContent={"center"} gap={1}>
                                                            <Typography component={"span"} variant="h6" sx={{
                                                                whiteSpace: "nowrap"
                                                            }}>
                                                                View
                                                            </Typography>
                                                        </Stack>
                                                    </SideTabbedButton>
                                                </Stack>
                                            </PlainContentItem>
                                        </Box>
                                    </Box>
                                </NavigationMenuContent>
                            </NavigationMenuItem>

                            <NavigationMenuItem>
                                <NavigationMenuLink href="/blog" className="nav-pill">
                                    <Typography component={"span"} variant="h6">
                                        Blog
                                    </Typography>
                                </NavigationMenuLink>
                            </NavigationMenuItem>

                            
                        </NavigationMenuList>
                    </NavigationMenu>
                </Box>

                <Box sx={{ display: { xs: 'none', lg: 'block' }, cursor: 'pointer' }}>
                    <Link href="/contact" style={{ textDecoration: 'none', display: 'block' }}>
                        <SideTabbedButton fullWidth paddingX={18} hoverShiftX={1.5} hoverShiftY={-1}>
                            <Stack direction="row" alignItems="center" justifyContent="center" gap={1}>
                                <Typography component="span" variant="h6" marginRight={0.5} sx={{ whiteSpace: "nowrap", display: "inline-block" }}>
                                    Contact us&nbsp;
                                </Typography>
                                <span className="arrow-clip">
                                    <span className="arrow-slide">
                                        <Image src={arrow} alt="arrow animation" width={20} height={20} style={{ display: "block" }} />
                                    </span>
                                </span>
                            </Stack>
                        </SideTabbedButton>
                    </Link>
                </Box>
                <Stack display={{ xs: "flex", lg: "none" }} direction="row" alignItems="center" justifyContent="end" gap={1}>
                    <Link href="/contact" style={{ textDecoration: 'none' }}>
                        <Button size="small" variant="contained" color="primary" sx={{
                            backgroundColor: "transparent",
                            backgroundImage: `url(${mobileContactButtonBg.src})`,
                            backgroundSize: "100% 100%",
                            backgroundPosition: "center",
                            backgroundRepeat: "no-repeat",
                            color: theme.palette.text.primary,
                            fontWeight: 400,
                            paddingTop: 0.5,
                            paddingBottom: 0.75,
                            textTransform: "none",
                        }}>
                            Contact us
                        </Button>
                    </Link>
                    <IconButton 
                        onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                        sx={{ padding: 0 }}
                    >
                        <Box 
                            sx={{
                                width: 35,
                                height: 35,
                                backgroundImage: `url(${mobileMenuButton.src})`,
                                backgroundSize: "100% 100%",
                                backgroundPosition: "center",
                                backgroundRepeat: "no-repeat",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center"
                            }}
                        >
                            <Image 
                                src={isMobileMenuOpen ? mobileMenuClose : mobileMenuOpen} 
                                alt={isMobileMenuOpen ? "Close menu" : "Open menu"} 
                                width={18}
                                height={18} 
                                style={{ display: "block" }} 
                            />
                        </Box>
                    </IconButton>
                </Stack>
            </Container>
            <MobileMenu open={isMobileMenuOpen} width={"100%"} closeAction={() => setIsMobileMenuOpen(false)} items={mobileMenuItems} />
        </Box>
    );
}
